import * as React from "react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ownerDocxModel, shareSplit, toggleFacilitatorOnly } from "../agendaShare.js";
import { buildDocx, DOCX_MIME } from "../agendaDocx.js";
import { agendaToCsv, deliveryClock, isRoundOfBlock, recomputeAgenda, roundConfigFor, roundsForBlock, type AgendaBlockInput, type DeliveryClock, type Method, type RoundSpec } from "@klapzo/kit-core";
import { describeDelivery } from "../announce.js";
import { loadAgenda, saveAgenda, type LiveSession, type StoredAgenda } from "../agendaStore.js";
import {
  AgendaRefusedError, createAgenda, deleteAgenda, draftBlocks, getAgenda, getVisibleMethod, listAgendas, shareAgenda,
  unshareAgenda, updateAgenda, type AgendaInput, type AgendaSummary,
} from "../agendasApi.js";
import { api, asLimitError, asRoundRefusal, asSessionOver, normalizeJoinCode, type QuestionConfig, type RoundResult, type SessionResults } from "../api.js";
import { getLocale, t } from "../i18n.js";
import { KitShell, LiveRegion, limitSentence } from "../components.js";
import { useRoom, type RoundVerdict } from "../useRoom.js";
import { resultsState, roundMeta, rowsFor, type ResultsState } from "./planner-results.js";

/**
 * ---------------------------------------------------------------------------------------------
 * Running a planned block in a live room, and reading back what the room answered
 * (US-MB-6, W12 fatia 1 + fatia 2).
 *
 * The rules live in pure functions because `klapzo-web` has no jsdom: the wiring below is asserted
 * by eye, so anything a reviewer cannot check by reading is pushed out and tested (same discipline
 * W10a/W10b/W11 recorded for their own panels). The round BODY is assembled by kit-core's
 * `roundConfigFor`, beside the validator that forbids the same keys — one assembly, shared with the
 * cross-app check in kit-api that drives every seeded spec through live-api's own `validateQuestion`.
 *
 * There is NO kit-api hop. The browser already holds the one `klapzo_session` cookie both APIs
 * verify (`Domain=.klapzo.app`, same `AUTH_SECRET`), so the round goes straight to the existing
 * `POST /sessions/:id/questions` on api.klapzo.app — the same request the Presenter's own buttons
 * make. No new route on either Worker, no new DO, no new transport (constitution #7(b)).
 * ---------------------------------------------------------------------------------------------
 */

/** Why "Rodar agora" is not clickable — one state, one caption, never a hidden button. */
export type RunState = "ready" | "signedOut" | "noSpec" | "noSession";

/**
 * Signed-out comes FIRST on purpose: with no session the method list cannot be read either, so
 * every block would otherwise report "this card has no round" — true-looking and the wrong cause.
 */
export function runState(spec: RoundSpec | null | undefined, live: LiveSession | null, signedIn: boolean): RunState {
  if (!signedIn) return "signedOut";
  if (!spec) return "noSpec";
  if (!live) return "noSession";
  return "ready";
}

/**
 * The options a host can tap to record the room's choice on a single-screen block (US-LV-22) — empty
 * for every other block, so the extra buttons exist only where the card asked for `hosted`. Pure and
 * exported: this app has no jsdom, and "which blocks get a record tap" is a rendering rule.
 */
export function hostedOptionsOf(spec: RoundSpec | null | undefined): Array<{ id: string; label: string }> {
  if (!spec || spec.type !== "mc" || spec.config?.hosted !== true) return [];
  const options = spec.config.options;
  if (!Array.isArray(options)) return [];
  return options.flatMap((o: unknown) => {
    const { id, label } = (o ?? {}) as { id?: unknown; label?: unknown };
    return typeof id === "string" && typeof label === "string" ? [{ id, label }] : [];
  });
}

/**
 * Does this row SHOW the arm? (fatia 3, round-1 review L.)
 *
 * An arm on a block that stopped being runnable — signed out, the card lost its spec, the room was
 * detached — says nothing: `live.armed` reads "rode quando quiser" and would sit beside a disabled
 * button, an instruction the facilitator cannot follow while the caption underneath already says
 * why. One expression, exported for the same reason `runState` and `resultsState` are: it is a
 * rendering rule and this app has no jsdom to assert it in place.
 */
export function showsArm(run: BlockRun | undefined, state: RunState): boolean {
  return run?.kind === "armed" && state === "ready";
}

/**
 * The round's id: `<blockId>.<8 hex>`.
 *
 * The prefix names the block, and since **fatia 2** it survives a save: `addBlock` mints it
 * client-side (`agendaStore`, localStorage) and kit-api stores it in `agenda_blocks.client_key`,
 * unique per agenda (migration 0009, US-MB-6 AC1). NOT in `agenda_blocks.id` — that is a global
 * primary key and this string is published to every socket on the room, so it is the server's to
 * mint and the planner reads its own key back with `draftBlocks`. live-api keeps the whole string
 * verbatim in `session_rounds.question_id` (constrained only to be non-empty), so a closed round
 * resolves back to the block after a save, a reload, and a new browser — which is what
 * `roundsForBlock` reads and what fatia 1 could not do.
 *
 * The join is a PREFIX match, never a `startsWith` on the bare id: `roundsForBlock` (kit-core) is
 * the inverse of this function and the dot is part of it, or block `abc` would claim `abcd`'s round.
 *
 * The suffix is what makes it correct to run the same block twice. W10b's offline queue compares
 * `qid` to decide whether a queued answer still belongs to the open round, so a phone holding a
 * frame from run 1 must not have it accepted by run 2 — which is exactly what a bare block id
 * would do. Random, not a counter: a counter would restart at 1 on the next reload.
 */
export function qidFor(blockId: string, uuid: string): string {
  return `${blockId}.${uuid.replace(/-/g, "").slice(0, 8)}`;
}

/**
 * A live-api refusal as a sentence in the host's language (constitution #6 — codes on the wire).
 *
 * Reuses the three readers the Presenter already has, in order of what the host can do about it:
 * the room has ended, the plan is out of rounds, the round's window is impossible. The fallback
 * carries the raw code, because a refusal nothing recognises renders as NOTHING — the host clicks,
 * the screen does not move, and they click again.
 *
 * `fallback` is the caller's, and that is the point: the same 403 means "you are not a facilitator,
 * so you cannot open a room" on the create button and something else entirely on a round. A single
 * sentence that said "a rodada não abriu, nada foi enviado para a sala" on every path was wrong on
 * two of them (round-1 review, M4).
 */
export type RefusalFallback = "live.refused" | "live.createFailed" | "live.attachFailed" | "live.closeFailed" | "live.resultsFailed";

export function runRefusal(status: number, data: unknown, fallback: RefusalFallback = "live.refused"): string {
  const over = asSessionOver(status, data);
  if (over) return t(over === "expired" ? "sessionOver.expired" : "sessionOver.closed");
  const limit = asLimitError(status, data);
  if (limit) return limitSentence(limit);
  const refusal = asRoundRefusal(status, data);
  if (refusal) return t(`error.${refusal}`);
  if (status === 401) return t("live.signedOut");
  // A member-role session reads agendas fine (any role) and cannot open a room — so this button is
  // reachable, refused, and needs to say WHY rather than name a code nobody can act on.
  if (status === 403) return t("live.notHost");
  if (status === 404) return t("live.roomNotFound");
  return t(fallback, { code: (data as { error?: string }).error ?? String(status) });
}

/**
 * What a block's last click produced. At most ONE block is `live` at a time, which is not a UI
 * nicety: live-api's `openQuestion` REPLACES the open round (pre-existing room behaviour, not
 * something this screen introduced), so two blocks both badged "no ar" would be a lie about the
 * room — and `superseded` is the honest word for the one that lost (round-1 review, M5).
 *
 * `unknown` is its own state: a `fetch` that rejects AFTER the request left the browser, or a 5xx
 * (the Worker threw — the DO may have failed before the broadcast, on the storage write after it,
 * which discards the held frames, or completed and lost its RESPONSE, with the round on every
 * phone), says nothing about what the room holds — for the block that was clicked, or for the one
 * that was live before it.
 */
export type BlockRun =
  | { kind: "live"; timeLimitMs: number | null }
  | { kind: "superseded" }
  | { kind: "closed" }
  | { kind: "failed"; message: string }
  | { kind: "unknown" }
  /**
   * The room closed the block before this one and this is the next runnable block (fatia 3). A run
   * OUTCOME the block has not had yet, rather than a second piece of state beside `runs`: the arm
   * and the badges are written by the same frames, and two states would need two updates React is
   * free to apply in either order — a value update from a stale closure discards a functional one
   * queued before it, which is exactly how a 201's `live` lost to a `closed` frame's arm.
   */
  | { kind: "armed" };

/**
 * Record an outcome, keeping the single-live invariant: a new `live` demotes whatever was live to
 * `superseded`, and an `unknown` launch demotes it to `unknown` too — the request LEFT, so the room
 * may already hold the new round, and a badge still reading "no ar" on the old block would offer a
 * close button over a round that is not its own (round-2 review). A confirmed `live` also settles
 * every earlier `unknown`: the 201 IS the room saying what it shows, so "we don't know what the room
 * is showing" beside it would be false (round-3 review). A `failed` launch demotes nothing: a
 * refusal opened nothing.
 *
 * A confirmed `live` also DROPS every `armed` entry (fatia 3): the arm points at "the round to open
 * next", and a round is open now. Cleared by the same write that makes a block live, so there is
 * one functional update and no second piece of state to lose a race with.
 *
 * Pure and exported because it is the invariant, and the JSX around it is not asserted (no jsdom).
 */
export function withRun(runs: Readonly<Record<string, BlockRun>>, blockId: string, outcome: BlockRun): Record<string, BlockRun> {
  const demoted: BlockRun | null =
    outcome.kind === "live" ? { kind: "superseded" } : outcome.kind === "unknown" ? { kind: "unknown" } : null;
  const next: Record<string, BlockRun> = {};
  for (const [id, run] of Object.entries(runs)) {
    if (outcome.kind === "live" && run.kind === "armed") continue; // the launched block's own entry is overwritten below
    next[id] = demoted && id !== blockId && (run.kind === "live" || run.kind === "unknown") ? demoted : run;
  }
  next[blockId] = outcome;
  return next;
}

/** The block whose round the room currently holds, if this screen opened it. */
export function liveBlockId(runs: Readonly<Record<string, BlockRun>>): string | null {
  return Object.entries(runs).find(([, run]) => run.kind === "live")?.[0] ?? null;
}

/**
 * The room said a round CLOSED. What does that mean for this agenda? (US-MB-6 fatia 3, AC3.)
 *
 * `null` = NOTHING changes, which is the common case and has to be cheap to be sure of: the frame is
 * broadcast to every socket on the room, so a Presenter opening and closing its own rounds on the
 * other laptop reaches this screen too. Only the block this planner has `live` reacts, and only when
 * the id is ITS round — kit-core's `isRoundOfBlock`, the same predicate `roundsForBlock` reads with,
 * so a sibling block whose id EXTENDS this one's (`abcd.1` for `abc`) is not mistaken for it.
 *
 * The armed block is the next one in AGENDA order after the one that closed whose `canRun` says it
 * could run right now — blocks without a round (a lecture, a break) are skipped, and after the last
 * runnable one nothing is armed. Nothing here fires anything: arming is a sentence and an emphasis,
 * and the click is the same click as before (assumption 1 — the projection is in front of a room).
 *
 * Returns the WHOLE new map, so the caller's update is `setRuns((r) => onRoomClosed(r, …) ?? r)` —
 * functional, over the runs React actually holds. Round-1 review, M3: a value update built from a
 * stale closure is applied in queue order and discards a functional update queued before it, so a
 * 201 that landed in the same window lost its `live` to this frame's arm.
 *
 * Pure and exported for the usual reason: `klapzo-web` has no jsdom, so the rule is asserted here
 * and the effect that calls it is read by eye.
 */
export function onRoomClosed(
  runs: Readonly<Record<string, BlockRun>>,
  blocks: readonly AgendaBlockInput[],
  questionId: string,
  canRun: (block: AgendaBlockInput) => boolean,
): Record<string, BlockRun> | null {
  const id = liveBlockId(runs);
  if (id === null || !isRoundOfBlock(questionId, id)) return null;
  return closeBlock(runs, blocks, id, canRun);
}

/**
 * The transition itself, by BLOCK id: the block is closed and the next runnable block after it is
 * armed. Two callers, one rule — `onRoomClosed` (the frame, once it knows which block it names) and
 * `closeRound`'s fallback for a socket that never connected (round-2 review): both must arm the same
 * block, and a rule spelled twice would drift.
 *
 * "Runnable" is `canRun` AND no outcome yet in this room: a block that already ran (out of order)
 * keeps its "encerrada", and a block whose launch was refused keeps its refusal — arming either would
 * overwrite a badge with an instruction the facilitator cannot follow (round-2 review).
 */
export function closeBlock(
  runs: Readonly<Record<string, BlockRun>>,
  blocks: readonly AgendaBlockInput[],
  blockId: string,
  canRun: (block: AgendaBlockInput) => boolean,
): Record<string, BlockRun> {
  const closed = withRun(runs, blockId, { kind: "closed" });
  const at = blocks.findIndex((b) => b.id === blockId);
  // A block deleted while its round was on air still gets its badge corrected; there is no "after"
  // to arm from, and arming from index 0 would point at the top of the agenda.
  const next = at === -1 ? undefined : blocks.slice(at + 1).find((b) => canRun(b) && !(b.id in runs));
  return next ? { ...closed, [next.id]: { kind: "armed" } } : closed;
}

/**
 * The room said a round OPENED. Which block is it, and what happens to the others? (US-MB-6 fatia 3
 * AC3b, fatia 5 AC3.)
 *
 * The frame is matched against EVERY block, not only the live one: a socket that has just connected
 * replays the round the room is holding (`state` → `question` → `aggregate` → `closed` iff it is
 * over, `realtime/room.ts:214-254`), and that replay is the only account there is of a round that is
 * open right now — `GET /sessions/:id/results` carries closed rounds and nothing else. So the block
 * the frame names goes `live` with the frame's own `timeLimitMs`, and `withRun` demotes whatever
 * else was live and drops the arm, exactly as a launch's own 201 does. Then the `closed` transition
 * closes it, and `rearm` arms the next once the method specs are known (after a reload they are
 * not yet when the replay lands), so a replay that ends on `closed` rebuilds "encerrada + armado"
 * from the socket alone.
 *
 * A frame for the round already `live` changes nothing. A frame NO block of this agenda claims still
 * only supersedes the live block: the room is showing a round this planner did not open.
 *
 * In an ORDINARY room a supersede emits NO `closed` frame: `Room.openQuestion` broadcasts only
 * `question` + `aggregate` (`realtime/room.ts:713-733`), and the pre-close that would emit one is
 * scoped to durable rounds in long-running sessions (`realtime/session-room.ts:716-722`) — which
 * the planner never creates (`api.createSession()` with no `longRunning`). So when the Presenter
 * opens its own round over the planner's, the ONLY signal is this frame, and without it the badge
 * would still read "no ar" for a round the room has already replaced — the lie `live.superseded`
 * exists to tell honestly.
 *
 * Both orders with the planner's OWN launch converge: the frame first supersedes the old block and
 * the 201 then makes the new one live; the 201 first makes it live and the frame is then that same
 * block's own round, so this returns `null`.
 */
export function onRoomQuestion(
  runs: Readonly<Record<string, BlockRun>>,
  blocks: readonly AgendaBlockInput[],
  question: { id: string; timeLimitMs?: number },
): Record<string, BlockRun> | null {
  const block = blocks.find((b) => isRoundOfBlock(question.id, b.id));
  if (block) {
    if (runs[block.id]?.kind === "live") return null;
    return withRun(runs, block.id, { kind: "live", timeLimitMs: question.timeLimitMs ?? null });
  }
  const id = liveBlockId(runs);
  if (id === null) return null;
  return withRun(runs, id, { kind: "superseded" });
}

/**
 * The room's CLOSED rounds, as badges. (US-MB-6 fatia 5, AC1/AC2.)
 *
 * `GET /sessions/:id/results` is the other half of the account the socket gives: it answers with
 * every round that CLOSED in this room (`app.ts:675-702`, written only by the close's own D1 flush,
 * which the close route awaits before it returns), and with nothing about the round on air. The
 * socket has the opposite shape — it replays the room's LAST round and no history, and after a
 * Durable Object eviction it advertises no round at all (`realtime/room.test.ts:123-143`). So the
 * read is always made, and the two are joined by the one predicate both sides already use,
 * `isRoundOfBlock`.
 *
 * It FILLS, it does not overwrite: a block gets `closed` only when it has no run yet or is `armed`,
 * so `live`, `superseded`, `failed`, `closed` — everything the room itself reported — survives the
 * read, in either arrival order. A round no block of this agenda claims (the Presenter opened its
 * own) says nothing about this agenda and is skipped. A block that ran twice is one key in one map,
 * so it wears one badge.
 *
 * It arms NOTHING (round-1 review of this slice, H2). An arm says "the room is quiet and this block
 * is next", and this payload cannot say the first half: it carries no open round, so a room whose
 * socket has not replayed yet — a venue that blocks the WSS upgrade while HTTP passes, the case
 * `closeRound`'s fallback already models — reads exactly like a quiet one, and the arm would land
 * on the block whose round is on air, with "rode quando quiser" over it: an instruction to
 * supersede the room's own round. The arm after a reload is the socket's to give — its replay ends
 * on `closed` when the last round is over, and `rearm` derives the next from that verdict once the
 * specs are known; after an eviction the arm comes from `rearmFromResults`, once the socket's verdict says the
 * room holds no round (THE-1438).
 *
 * `null` = nothing changes, and the caller relies on it: `setRuns((r) => applyResults(r, …) ?? r)`.
 */
export function applyResults(
  runs: Readonly<Record<string, BlockRun>>,
  blocks: readonly AgendaBlockInput[],
  // The one field this reads, rather than live-api's whole `RoundResult` — same shape of promise
  // `roundsForBlock` makes in kit-core, and what lets a test state a case in one line.
  rounds: readonly { questionId: string }[],
): Record<string, BlockRun> | null {
  let next: Record<string, BlockRun> | null = null;
  for (const round of rounds) {
    const block = blocks.find((b) => isRoundOfBlock(round.questionId, b.id));
    if (!block) continue;
    const run = (next ?? runs)[block.id];
    if (run !== undefined && run.kind !== "armed") continue;
    next = { ...(next ?? runs), [block.id]: { kind: "closed" } };
  }
  return next;
}

/**
 * The arm, once the specs are in. (US-MB-6 fatia 5, AC2.)
 *
 * After a reload the socket's replayed `closed` lands one handshake after mount — before
 * `listAgendas` has answered, and two round-trips before any card's `roundSpec` — so `onRoomClosed`
 * runs with a `canRun` that says "no spec" for every block, and `closeBlock` finds nothing to arm
 * (round-2 review of this slice, H3). This is the same derivation made once the specs are known, and
 * from the socket's own verdict, not from `/results`: `lastClosedQuestionId` is set by a `closed`
 * frame and cleared by the next `question`, so a non-null one says "this round is over and nothing
 * newer is on air" — the one thing `/results` cannot say.
 *
 * Only when the map has nothing `live` and nothing `armed` (both already answer "what runs next"
 * with better evidence), and only when the named block is `closed` — `live` is a `closed` frame not
 * yet reduced, `superseded` or `failed` is not a close at all, and a block this agenda does not own
 * says nothing about it. `closeBlock` does the scan; the block is already closed, so the only thing
 * it can add is the arm, and `null` means it added nothing.
 */
export function rearm(
  runs: Readonly<Record<string, BlockRun>>,
  blocks: readonly AgendaBlockInput[],
  questionId: string,
  canRun: (block: AgendaBlockInput) => boolean,
): Record<string, BlockRun> | null {
  const block = blocks.find((b) => isRoundOfBlock(questionId, b.id));
  if (!block || runs[block.id]?.kind !== "closed") return null;
  if (liveBlockId(runs) !== null || Object.values(runs).some((r) => r.kind === "armed" || r.kind === "unknown")) return null;
  const next = closeBlock(runs, blocks, block.id, canRun);
  return Object.values(next).some((r) => r.kind === "armed") ? next : null;
}

/**
 * The arm after a Durable Object eviction (THE-1438).
 *
 * An evicted room advertises no round (`state` with `currentQuestionId === null`), so the socket
 * never replays a `closed` and `rearm` has nothing to read. `/results` knows the rounds that closed
 * but cannot say the room is quiet, so it arms ONLY once the socket's own verdict is `none`: the
 * block after the most recently closed round of this agenda is armed, through `rearm`'s own rules
 * (nothing live, nothing armed, the block is `closed`). Any other verdict — `unknown` included —
 * arms nothing; `round` keeps the existing rule, armed by the `closed` frame.
 */
export function rearmFromResults(
  runs: Readonly<Record<string, BlockRun>>,
  blocks: readonly AgendaBlockInput[],
  rounds: readonly { questionId: string; closedAt: number }[],
  verdict: RoundVerdict,
  canRun: (block: AgendaBlockInput) => boolean,
): Record<string, BlockRun> | null {
  if (verdict !== "none") return null;
  let latest: { questionId: string; closedAt: number } | null = null;
  for (const round of rounds) {
    if (!blocks.some((b) => isRoundOfBlock(round.questionId, b.id))) continue;
    if (latest === null || round.closedAt > latest.closedAt) latest = round;
  }
  return latest === null ? null : rearm(runs, blocks, latest.questionId, canRun);
}

/**
 * What `POST /sessions/:id/questions` answering `status` means for the block.
 *
 * Every 4xx is decided BEFORE the room is touched (`validateQuestion`, quota, ownership, a closed
 * session), so "nada foi enviado para a sala" is exact there. A 5xx is not a refusal: the Worker
 * threw, and the DO may have failed before the broadcast, on the storage write after it (the output
 * gate discards the held frames), or completed with the round on every phone and lost only its
 * response — so the honest state is `unknown`, the same as a rejected `fetch` (round-4 review; a
 * retry would spend the quota twice on a round that is already open).
 */
export function launchOutcome(status: number, data: unknown, timeLimitMs: number | null): BlockRun {
  if (status === 201) return { kind: "live", timeLimitMs };
  if (status >= 500) return { kind: "unknown" };
  return { kind: "failed", message: runRefusal(status, data) };
}

/** null = signed out (the planner still works, locally). */
type Saved = AgendaSummary[] | null;

export function Planner(): React.ReactElement {
  const [agenda, setAgenda] = React.useState<StoredAgenda>(() => loadAgenda());
  const [saved, setSaved] = React.useState<Saved>(null);
  const [busy, setBusy] = React.useState(false);
  const [status, setStatus] = React.useState<"idle" | "saved" | "failed">("idle");
  /** The server's code for a REFUSED save (400), so the message can name it. Null = no code. */
  const [saveCode, setSaveCode] = React.useState<string | null>(null);
  const [shareToken, setShareToken] = React.useState<string | null>(null);
  /**
   * Only the methods this agenda's blocks reference, by id, cached for the life of the screen.
   * `null` is a RESOLVED miss (signed out, deleted, another tenant's) and not a retry: without it,
   * a 404 would be re-requested on every render for as long as the block exists.
   */
  const [methods, setMethods] = React.useState<Map<string, Method | null>>(() => new Map());
  /**
   * What each block's round did, INCLUDING the arm (`{kind:"armed"}` — the block the planner is
   * pointing at after the room closed the one before it, which never fires itself). One map, so
   * every frame and every launch is one functional update and no two states can be applied out of
   * order (round-1 review, M3).
   */
  const [runs, setRuns] = React.useState<Record<string, BlockRun>>({});
  const [liveError, setLiveError] = React.useState<string | null>(null);
  const [typedCode, setTypedCode] = React.useState("");
  /**
   * The room's results, read WHOLE and joined per block (US-MB-6 fatia 2).
   *
   * One state for the session, not one per block: `GET /sessions/:id/results` has no filter — it
   * answers with every round of the session — so a read per block would be the same payload N
   * times. `roundsForBlock` does the join, and `openBlock` is which panel is expanded.
   */
  const [sessionResults, setSessionResults] = React.useState<SessionResults | null>(null);
  const [resultsError, setResultsError] = React.useState<string | null>(null);
  const [openBlock, setOpenBlock] = React.useState<string | null>(null);

  const update = (next: StoredAgenda): void => {
    saveAgenda(next);
    setAgenda(next);
    // Any edit invalidates a "saved" badge — leaving it up would claim the server has changes it
    // does not, which is worse than showing nothing. The refusal code goes with it: it described a
    // body that no longer exists.
    setStatus("idle");
    setSaveCode(null);
  };

  const refresh = React.useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      setSaved(await listAgendas(signal ?? new AbortController().signal));
    } catch {
      setSaved(null); // kit-api unreachable: fall back to the local-only planner rather than block
    }
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  // The ids worth asking for: referenced by a block, not already known, and only once we know there
  // is a session at all — a signed-out planner makes no method request. Joined into a string so the
  // effect's dependency is the SET, not a new array identity on every render.
  const missingIds = [...new Set(agenda.blocks.map((b) => b.methodId).filter((v): v is string => !!v))]
    .filter((id) => !methods.has(id))
    .sort()
    .join(",");

  // Each id is asked ONCE for the component's life. The previous shape held one AbortController per
  // effect run and aborted it on cleanup — so every resolution shrank `missingIds`, re-ran the effect,
  // cancelled every still-pending sibling (and discarded responses already arrived) and re-issued
  // them: N(N+1)/2 requests and ~N serial round-trips for N distinct cards, none cacheable
  // (`no-store`) (round-2 review, M7). Nothing is aborted now; a response that lands after unmount is
  // a no-op `setState`.
  const asked = React.useRef(new Set<string>());
  React.useEffect(() => {
    if (saved === null || missingIds === "") return;
    for (const id of missingIds.split(",")) {
      if (asked.current.has(id)) continue;
      asked.current.add(id);
      void getVisibleMethod(id).then(
        (method) => setMethods((m) => new Map(m).set(id, method)),
        // Unreachable kit-api: forget the id so a later render retries it. A miss cached here would
        // turn one dropped request into a permanently dead button.
        () => asked.current.delete(id),
      );
    }
  }, [missingIds, saved]);

  // WITH the block ids (US-MB-6 fatia 2): the draft's uuid goes up as each block's `id` and the
  // server keeps it in `client_key`, which is what makes `<blockId>.<8 hex>` in a closed round still
  // name this block after a save and a reload. Stripping them — what this did in fatia 1 — is what
  // left the join with nothing to resolve.
  const toInput = (title = agenda.title, blocks = agenda.blocks): AgendaInput => ({
    title,
    startClock: agenda.startClock,
    blocks,
  });

  /** Resolves to the saved agenda's server id, or null when the save did not happen. */
  const persist = async (): Promise<string | null> => {
    setBusy(true);
    setSaveCode(null);
    try {
      const input = toInput();
      // An agenda deleted in another tab comes back null from PUT; fall through to a create rather
      // than telling the facilitator their save failed.
      const result = (agenda.serverId ? await updateAgenda(agenda.serverId, input) : null) ?? (await createAgenda(input));
      if (!result) {
        setStatus("failed");
        return null;
      }
      update({ ...agenda, serverId: result.id });
      setShareToken(result.shareToken);
      setStatus("saved");
      await refresh();
      return result.id;
    } catch (err) {
      // A refusal is NAMED, never repaired. The draft is not re-minted and not retried: the block
      // ids it holds are the ones already published into the rounds it ran, so "fixing" them would
      // trade a visible failure for a silent one (the results panel would just go empty).
      if (err instanceof AgendaRefusedError) setSaveCode(err.code);
      setStatus("failed");
      return null;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Duplicate needs no server route: the client already holds the whole agenda, so a copy is just
   * a POST of what is on screen. The draft then points at the NEW row, and the share token is
   * dropped — a copy must not inherit a link that was handed out for the original.
   *
   * The block ids ride along UNCHANGED (US-MB-6 fatia 2). The planner's key is unique per AGENDA,
   * not globally (0009), so the copy may hold the same keys — and it should: the draft on screen is
   * still the same blocks, the run badges still key on them, and a round that is on air keeps the
   * button that closes it. What the copy inherits is the source's round history too, which is the
   * honest reading of a copy that also inherited `liveSessionId`.
   */
  const duplicate = async (): Promise<void> => {
    setBusy(true);
    try {
      const copy = await createAgenda(toInput(`${agenda.title} (2)`));
      if (copy) {
        update({ ...agenda, title: copy.title, serverId: copy.id });
        setShareToken(null);
        await refresh();
      }
    } finally {
      setBusy(false);
    }
  };

  const toggleShare = async (): Promise<void> => {
    if (!agenda.serverId) return;
    setBusy(true);
    try {
      if (shareToken) {
        await unshareAgenda(agenda.serverId);
        setShareToken(null);
      } else {
        // Confirm BEFORE the link exists (THE-1698): the facilitator sees how many blocks the
        // visitors will and will not get. A refusal leaves everything as it was.
        const { total, hidden, visible } = shareSplit(agenda.blocks);
        const vars = { title: agenda.title, total: String(total), hidden: String(hidden), visible: String(visible) };
        if (!window.confirm(t(hidden > 0 ? "planner.shareConfirm" : "planner.shareConfirmNone", vars))) return;
        // The link serves what is SAVED, and the dialog above counted the DRAFT. Save first, so the
        // number the facilitator confirmed is the number the visitors get; if the save fails, no
        // link is made (US-MB-7, CTO review of #91).
        const id = await persist();
        if (id === null) return;
        setShareToken(await shareAgenda(id));
      }
    } finally {
      setBusy(false);
    }
  };

  const open = async (id: string): Promise<void> => {
    setBusy(true);
    try {
      const a = await getAgenda(id);
      if (a) {
        // Hydrate the live link, so an owner who reloads can still see and revoke it. Opening an
        // unshared agenda clears whatever the previous one had on screen.
        setShareToken(a.shareToken);
        // A different agenda is not running in the previous one's room, and its blocks carry
        // whatever `liveSessionId` D1 already held — so the attached room and the on-air badges go.
        setRuns({});
        forgetResults();
        update({
          title: a.title,
          startClock: a.startClock ?? "09:00",
          // The KEY the draft saved, not the row id the server minted on the last write — see
          // `draftBlocks`. Restoring the wrong one is what breaks the round join after a reload.
          blocks: draftBlocks(a.blocks),
          serverId: a.id,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const discard = async (id: string): Promise<void> => {
    setBusy(true);
    try {
      await deleteAgenda(id);
      // Deleting the agenda currently open unlinks the draft rather than clearing it — the
      // facilitator keeps what is on screen, it just stops pointing at a row that is gone.
      if (agenda.serverId === id) update({ ...agenda, serverId: null });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const live = agenda.live ?? null;
  const specOf = (block: AgendaBlockInput): RoundSpec | null =>
    (block.methodId ? methods.get(block.methodId)?.roundSpec : null) ?? null;

  /**
   * The planner LISTENS to the room it has attached (fatia 3, AC2) — the identical call
   * `Presenter.tsx` and `Projection.tsx` make, as a pure OBSERVER: it never sends `join`, never
   * sends anything, and renders none of the frames it receives beyond the one below. No new DO
   * method, wire event, REST route or auth: a socket that has not joined is an observer by the
   * room's own design (constitution #7(b)).
   *
   * `null` when no room is attached, and detaching goes back to `null` — the hook's own effect
   * closes the socket on that transition (`useRoom.ts`, the cleanup of the connect effect), so
   * there is nothing to unwind here.
   *
   * `observer: true` turns the offline queue off (round-1 review, F5). Not a saving: the queue is
   * stored under the CONNECT PATH, which is byte-identical to the one a phone in this browser used
   * to join the same room — so without it the planner hydrated a participant's unsent answers and
   * its persist effect wiped them.
   */
  const { state: room } = useRoom(live ? `/sessions/${live.id}/connect` : null, { observer: true });

  /** Could this block open a round right now? The arm's notion of "runnable", shared by both writers. */
  const canRun = (b: AgendaBlockInput): boolean => runState(specOf(b), live, saved !== null) === "ready";

  /**
   * Attach the room. Two ways in, because both happen: the facilitator opens the room from here, or
   * the room is already up (opened in the Presenter, on the other laptop) and they type its code.
   * `GET /j/:code` is the only id↔code resolver there is, and it answers 404 for a room that is not
   * open — which is the same answer the host needs either way.
   */
  const attachLive = (next: LiveSession | null): void => {
    setLiveError(null);
    // A run belongs to the room it ran in; carrying "no ar" — or an arm derived from a close in the
    // room being left — into another room is a lie. Both live in this one map, so both go here.
    setRuns({});
    forgetResults(); // …and so do its results: they are ONE room's rounds, read from that room
    // The frames already handled belong to the room being left. `useRoom` resets its state on the
    // path change, so the new socket's replay (`question`, `closed`) is news again and must not be
    // swallowed by the id guards (THE-1437). The old `closedAtAttach` guard is gone: the reset
    // already empties `lastClosedQuestionId`, so no close id outlives the attach.
    handledQuestion.current = null;
    handledClose.current = null;
    // Marks the draft dirty. Conservative rather than wrong: the room itself is never saved, but
    // running a block writes `liveSessionId` onto it, and a stale "saved" badge is the worse lie.
    update({ ...agenda, live: next });
  };

  const createLive = async (): Promise<void> => {
    setBusy(true);
    try {
      const { status, data } = await api.createSession();
      if (status === 201) attachLive({ id: data.sessionId, code: data.joinCode });
      else setLiveError(runRefusal(status, data, "live.createFailed"));
    } catch {
      // Nothing was opened that matters: a create either returned a room or it did not.
      setLiveError(t("live.createFailed", { code: "OFFLINE" }));
    } finally {
      setBusy(false);
    }
  };

  const attachByCode = async (): Promise<void> => {
    const code = normalizeJoinCode(typedCode);
    if (!code) return setLiveError(t("live.badCode"));
    setBusy(true);
    try {
      const { status, data } = await api.resolveCode(code);
      if (status === 200) {
        attachLive({ id: data.sessionId, code });
        setTypedCode("");
      } else setLiveError(runRefusal(status, data, "live.attachFailed"));
    } catch {
      setLiveError(t("live.attachFailed", { code: "OFFLINE" }));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Open this block's round in the attached room. A fresh round every click, by design — there is no
   * "resume", and `qidFor` gives each run its own id so a phone's stale frame cannot be accepted by
   * the next one.
   */
  const runBlock = async (block: AgendaBlockInput): Promise<void> => {
    const spec = specOf(block);
    if (!spec || !live) return;
    setBusy(true);
    try {
      // The cast is the seam between two validators: `roundConfigFor` assembled what kit-core knows
      // (and stripped what a card may not decide); live-api judges what these fields MEAN together
      // and answers with a code this block renders. `QuestionConfig` is this app's hand-synced
      // mirror of live-api's wire types, which kit-core deliberately does not model.
      const config = roundConfigFor(spec, block.title, qidFor(block.id, crypto.randomUUID())) as QuestionConfig;
      const { status, data } = await api.openQuestion(live.id, config);
      setRuns((r) => withRun(r, block.id, launchOutcome(status, data, spec.timeLimitMs ?? null)));
      if (status === 201) {
        // (The arm is dropped by `withRun` in the same update the `live` outcome is written — a
        // leftover arm would emphasise a second "Rodar agora" beside the round actually on air.)
        // The first writer `agenda_blocks.live_session_id` has ever had: the block now records the
        // room it ran in, and the next save carries it to D1.
        update({
          ...agenda,
          blocks: agenda.blocks.map((b) => (b.id === block.id ? { ...b, liveSessionId: live.id } : b)),
        });
      }
    } catch {
      // The request LEFT. Whether the round opened is unknowable from here, and saying "nothing was
      // sent" would send the host to click again into a room that may already be running it.
      setRuns((r) => withRun(r, block.id, { kind: "unknown" }));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Close the round this screen opened, through the existing `POST /sessions/:id/questions/close`.
   *
   * Without it an untimed block was a dead end: the badge said "you close it when you like" and no
   * reachable surface could — `/host` cannot attach to a room it did not create and the projection
   * is read-only (round-1 review, M5).
   */
  /**
   * Read the room's results, through the EXISTING `GET /sessions/:id/results` with the host's own
   * cookie — no kit-api hop and no new route on either Worker, exactly as the launch does.
   *
   * On demand only: a click, and a successful close of a block whose panel is already open. No poll
   * and no socket — live results are what fatia 3's `closed` frame is for, and a planner that
   * polled a room would be a second source of truth for what is on air.
   */
  const readResults = async (): Promise<void> => {
    if (!live) return;
    try {
      const { status, data } = await api.results(live.id);
      // `api.results` does not parse a failed body, so there is no code to name — `runRefusal` still
      // gives 401/403/404 their own sentences and falls back to this caller's own (M4's rule).
      if (data) {
        setSessionResults(data);
        setResultsError(null);
      } else setResultsError(runRefusal(status, {}, "live.resultsFailed"));
    } catch {
      setResultsError(t("live.resultsFailed", { code: "OFFLINE" }));
    }
  };

  /** Drop what was read: another room, or another agenda, has other rounds. */
  const forgetResults = (): void => {
    setSessionResults(null);
    setResultsError(null);
    setOpenBlock(null);
  };

  /** Toggle a block's panel. The read happens on the way OPEN, so a closed panel costs nothing. */
  const toggleResults = async (blockId: string): Promise<void> => {
    if (openBlock === blockId) {
      setOpenBlock(null);
      return;
    }
    setOpenBlock(blockId);
    setBusy(true);
    try {
      await readResults();
    } finally {
      setBusy(false);
    }
  };

  /**
   * Ask the room to close this block's round. Since fatia 3 a 200 writes nothing WHILE THE SOCKET IS
   * UP (round-1 review, M1): the room's own `closed` frame is the writer of the badge, the arm and
   * the results re-read. With no socket it falls back to this response (below).
   *
   * Three reasons, in order of how badly the optimistic write behaved:
   * 1. It pre-empted the frame. `onRoomClosed` only acts on the block that is `live`, so a block this
   *    function had already marked `closed` was invisible to the frame that arrived a moment later —
   *    the arm never derived on the planner's OWN close, and never at all on a reconnect (the 200
   *    always beats the replayed catch-up frame).
   * 2. A 200 does not mean "closed". A non-final `promptvote` close is a PHASE advance — the route
   *    answers `{ok:true}` all the same (`app.ts:491-496`) while `closeCurrentQuestion` returns
   *    false and broadcasts only an aggregate (`realtime/room.ts:798-808`) — so the badge said
   *    "encerrada" over a ballot that had just opened.
   * 3. It issued a second `GET /results` racing the effect's, two reads for one close.
   *
   * A refusal and a rejected `fetch` still write here: neither produces a frame, so nothing else
   * would ever say what happened. The 5xx sentence hedges for the reason it always did (M4/M6): a DO
   * that never answered leaves the round open, one that failed after its checkpoint leaves it closed.
   */
  const closeRound = async (blockId: string, optionId?: string): Promise<void> => {
    if (!live) return;
    setBusy(true);
    try {
      // `optionId` is the room's choice on a single-screen block (US-LV-22); absent closes as ever.
      const { status, data } = await api.closeQuestion(live.id, optionId);
      if (status !== 200) {
        setRuns((r) => withRun(r, blockId, { kind: "failed", message: runRefusal(status, data, "live.closeFailed") }));
      } else if (!room.connected) {
        // No frame is coming (round-2 review): the socket never connected — a venue that blocks the
        // WSS upgrade while HTTP passes, the exact case `useRoom` models with `offline` — or it is
        // down right now. Fall back to this response, main's behaviour on this path only, through
        // the SAME transition the frame would have made, so the arm derives here too. A promptvote
        // phase advance also answers 200 and reads "encerrada" on this path; the frame path does not
        // have that defect, and a later catch-up replays only the CURRENT round, so it can neither
        // cause nor correct this one. If the socket does come back, the replayed `closed` finds no
        // live block and changes nothing.
        setRuns((r) => (liveBlockId(r) === blockId ? closeBlock(r, agenda.blocks, blockId, canRun) : r));
        if (openBlock === blockId) void readResults();
      }
    } catch {
      setRuns((r) => withRun(r, blockId, { kind: "unknown" }));
    } finally {
      setBusy(false);
    }
  };

  /**
   * The room OPENED a round that is not the live block's — it has been superseded (fatia 3, AC3b).
   *
   * The signal a `closed` frame does NOT give: in an ordinary room a supersede broadcasts only
   * `question` + `aggregate` (`realtime/room.ts:713-733`), so without this the badge would still
   * read "no ar" for a round the Presenter replaced on the other laptop. Same ref guard, same
   * functional form, and the two effects converge in both orders with this screen's own launch —
   * see `onRoomQuestion`.
   *
   * Detaching and re-attaching the SAME room in one mount (round-1 review, M1; fixed in THE-1437):
   * `useRoom` now resets `RoomState` when the path changes and `attachLive` clears this ref, so the
   * new socket's replayed `question` is news again and a round still on air is re-announced.
   *
   * DECLARED BEFORE the `closed` effect, and the order is load-bearing (round-3 review of fatia 5,
   * M): a reconnect replays `question` then `closed` back to back, and React may render both frames
   * in ONE pass. Effects run in declaration order and their functional updates apply in that order,
   * so this one must make the block `live` before `onRoomClosed` looks for a live block to close —
   * the other way round, the close finds nothing, and the block is left "no ar" over a round that is
   * already over. When the frames render separately the order changes nothing.
   */
  const handledQuestion = React.useRef<string | null>(null);
  const question = room.question;
  const questionId = question?.id ?? null;
  React.useEffect(() => {
    if (!question || handledQuestion.current === question.id) return;
    handledQuestion.current = question.id;
    setRuns((r) => onRoomQuestion(r, agenda.blocks, question) ?? r);
  }, [questionId]);

  /**
   * The room CLOSED a round — mine, the Presenter's, or the timer's (fatia 3, AC3/AC4/AC5).
   *
   * Keyed on the ID, not on a counter: `lastClosedQuestionId` only ever CHANGES, so the ref is what
   * stops a re-render from replaying the same close (React may run an effect again for reasons that
   * are not a new frame). Set before the work, so an early return still counts the frame as seen.
   *
   * `setRuns` takes the FUNCTIONAL form, over the runs React actually holds rather than the ones
   * this closure captured: a value update is applied in queue order and would discard a 201's
   * functional update queued a moment earlier (round-1 review, M3).
   */
  const handledClose = React.useRef<string | null>(null);
  const closedId = room.lastClosedQuestionId;
  React.useEffect(() => {
    if (closedId === null || handledClose.current === closedId) return;
    handledClose.current = closedId;
    setRuns((r) => onRoomClosed(r, agenda.blocks, closedId, canRun) ?? r);
    // Only for the panel actually ON SCREEN, and only when the round that closed is the one it is
    // showing: a collapsed panel leaves `sessionResults` non-null, so gating on that alone spent a
    // read nothing renders — and a failed read then set `resultsError` for a panel nobody is
    // looking at. While the socket is up this is the only read a close triggers; the fallback in
    // `closeRound` reads once itself when it is not.
    if (openBlock !== null && isRoundOfBlock(closedId, openBlock)) void readResults();
  }, [closedId]);

  /**
   * The arm the frame above could not derive yet — see `rearm`. Re-run whenever what it reads
   * changes: the specs resolving (`missingIds` → `""`), `saved` (a new array after every save, not
   * only at sign-in), and the close itself. Idempotent: after a close that `onRoomClosed` armed, or
   * one with nothing runnable after it, `rearm` finds an arm already there or nothing to add, and
   * answers `null`. `useRoom` resets on a path change, so a close id here is always the
   * attached room's own (THE-1437): the room being left's verdict cannot leak in.
   */
  React.useEffect(() => {
    if (closedId === null || missingIds !== "" || saved === null) return;
    setRuns((r) => rearm(r, agenda.blocks, closedId, canRun) ?? r);
  }, [closedId, missingIds, saved]);

  /**
   * What the room already knows, on attach (US-MB-6 fatia 5, AC1/AC2).
   *
   * `runs` lives for as long as the screen does — a reload, a re-attach, or attaching to a room that
   * has been running since before this planner opened starts it empty, while `agenda.live` comes
   * back from localStorage and the observer socket reattaches by itself. The socket replays the
   * room's last round; this read is what accounts for every round before it. ONCE per attached room,
   * ref-guarded on the id like the two frame effects, and never while signed out — `/results` is
   * host-cookie gated, so a signed-out planner would spend a 401 on every attach.
   *
   * Straight to the client, not through `readResults`: this read has no panel to report into, and
   * its payload is for `runs` alone — `sessionResults` stays as it was, so the first panel opened
   * afterwards still shows its own loading beat rather than "nada rodou" out of a payload older than
   * the round it was opened on. A refusal or a dead network changes nothing; the "Resultados" button
   * still reports on click. And the answer is checked against the ref when it LANDS, not only when
   * it is asked for: `attachLive` empties the map for the next room, and a late answer from the room
   * being left would paint that room's rounds onto it (round-1 review, H1).
   */
  const reconstructed = React.useRef<string | null>(null);
  const [readRounds, setReadRounds] = React.useState<{ liveId: string; rounds: SessionResults["rounds"] } | null>(null);
  const liveId = live?.id ?? null;
  React.useEffect(() => {
    if (liveId === null) {
      reconstructed.current = null; // detached: re-attaching the same room reads it again
      return;
    }
    if (saved === null || reconstructed.current === liveId) return;
    reconstructed.current = liveId;
    void api.results(liveId).then(
      ({ data }) => {
        if (data === null || reconstructed.current !== liveId) return;
        setRuns((r) => applyResults(r, agenda.blocks, data.rounds) ?? r);
        setReadRounds({ liveId, rounds: data.rounds });
      },
      () => undefined,
    );
  }, [liveId, saved]);

  /**
   * The arm after an eviction (THE-1438): the rounds that read returned, joined with the socket's
   * verdict that the room holds none. Neither alone arms — see `rearmFromResults`. The rounds are
   * kept with the room they came from, so a late read for a room already left cannot arm the next.
   * Re-runs when the specs resolve, like the `closedId` arm above.
   */
  const roundVerdict = room.roundVerdict;
  React.useEffect(() => {
    if (readRounds === null || readRounds.liveId !== liveId || missingIds !== "" || saved === null) return;
    setRuns((r) => rearmFromResults(r, agenda.blocks, readRounds.rounds, roundVerdict, canRun) ?? r);
  }, [readRounds, roundVerdict, missingIds, saved]);

  const scheduled = recomputeAgenda(agenda.blocks, agenda.startClock);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const onDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = agenda.blocks.findIndex((b) => b.id === active.id);
    const newIndex = agenda.blocks.findIndex((b) => b.id === over.id);
    update({ ...agenda, blocks: arrayMove(agenda.blocks, oldIndex, newIndex) });
  };

  const setDuration = (id: string, durationMin: number): void =>
    update({ ...agenda, blocks: agenda.blocks.map((b) => (b.id === id ? { ...b, durationMin } : b)) });
  const toggleFacOnly = (id: string): void => update({ ...agenda, blocks: toggleFacilitatorOnly(agenda.blocks, id) });
  const remove = (id: string): void => update({ ...agenda, blocks: agenda.blocks.filter((b) => b.id !== id) });

  const download = (body: BlobPart, type: string, ext: string): void => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([body], { type }));
    // Strip path separators as well as spaces: a title of "Q3/Q4" would otherwise propose a
    // filename with a directory in it, which browsers handle inconsistently.
    a.download = `${agenda.title.replace(/[\s/\\]+/g, "-").toLowerCase() || "agenda"}.${ext}`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportJson = (): void =>
    download(JSON.stringify({ ...agenda, schedule: scheduled }, null, 2), "application/json", "json");

  const exportCsv = (): void =>
    download(
      agendaToCsv(scheduled, {
        order: t("planner.csvOrder"),
        start: t("planner.start"),
        title: t("planner.csvBlock"),
        duration: `${t("planner.csvDuration")} (${t("planner.min")})`,
        total: t("planner.total"),
      }),
      "text/csv;charset=utf-8",
      "csv",
    );

  // THE-6012: the owner's Word file is the complete plan (facilitator-only blocks marked), built here.
  const exportDocx = (): void => {
    const titles = new Map<string, string>();
    for (const [id, m] of methods) if (m) titles.set(id, m.title);
    download(
      buildDocx(ownerDocxModel(agenda.title, agenda.blocks, agenda.startClock, titles), {
        order: t("planner.csvOrder"),
        start: t("planner.start"),
        block: t("planner.csvBlock"),
        duration: t("planner.csvDuration"),
        method: t("planner.docxMethod"),
        total: t("planner.total"),
        min: t("planner.min"),
        facilitatorOnly: t("planner.docxFacOnly"),
      }),
      DOCX_MIME,
      "docx",
    );
  };

  return (
    <KitShell>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <input
          value={agenda.title}
          onChange={(e) => update({ ...agenda, title: e.target.value })}
          className="font-display text-display-md bg-transparent outline-none"
          aria-label="title"
        />
        <div className="no-print flex items-center gap-3">
          <label className="text-body-sm">
            {t("planner.start")}{" "}
            <input
              type="time"
              value={agenda.startClock}
              onChange={(e) => update({ ...agenda, startClock: e.target.value })}
              className="rounded-md border border-border p-1"
            />
          </label>
          <button onClick={exportCsv} className="rounded-md bg-surface px-3 py-1">{t("planner.exportCsv")}</button>
          <button onClick={exportDocx} className="rounded-md bg-surface px-3 py-1">{t("planner.exportDocx")}</button>
          <button onClick={exportJson} className="rounded-md bg-surface px-3 py-1">{t("planner.export")}</button>
          <button onClick={() => window.print()} className="rounded-md bg-surface px-3 py-1">{t("planner.printPdf")}</button>
          {saved !== null && agenda.serverId && (
            <>
              <button onClick={() => void duplicate()} disabled={busy} className="rounded-md bg-surface px-3 py-1 disabled:opacity-50">
                {t("planner.duplicate")}
              </button>
              <button onClick={() => void toggleShare()} disabled={busy} className="rounded-md bg-surface px-3 py-1 disabled:opacity-50">
                {shareToken ? t("planner.unshare") : t("planner.share")}
              </button>
            </>
          )}
          {saved !== null && (
            <button
              onClick={() => void persist()}
              disabled={busy}
              className="rounded-md bg-primary px-3 py-1 font-bold text-primary-foreground disabled:opacity-50"
            >
              {agenda.serverId ? t("planner.save") : t("planner.saveNew")}
            </button>
          )}
        </div>
      </div>

      {/* Signed out is not an error state — the planner has always worked off the local draft. */}
      <p className="no-print text-body-sm text-muted" role="status">
        {saved === null
          ? t("planner.localOnly")
          : status === "saved"
            ? t("planner.savedOk")
            : status === "failed"
              ? saveCode
                ? t("planner.saveRefused", { code: saveCode })
                : t("planner.saveFailed")
              : agenda.serverId
                ? t("planner.linked")
                : t("planner.unsaved")}
      </p>

      {shareToken && (
        <div className="no-print flex flex-col gap-1 rounded-md border border-border p-3">
          <span className="text-body-sm font-bold">{t("planner.shareLink")}</span>
          <input
            readOnly
            value={`${location.origin}/kit/a/${shareToken}`}
            onFocus={(e) => e.currentTarget.select()}
            aria-label={t("planner.shareLink")}
            className="rounded-md border border-border bg-surface p-2 text-body-sm"
          />
          <span className="text-body-sm text-muted">{t("planner.shareHint")}</span>
        </div>
      )}

      {/* Sessão ao vivo (US-MB-6). Signed out this is one explanatory line: the planner has always
          worked offline, and running a round is the one thing that cannot. */}
      <div className="no-print flex flex-col gap-2 rounded-md border border-border p-3">
        <span className="text-body-sm font-bold">{t("live.title")}</span>
        {saved === null ? (
          <span className="text-body-sm text-muted">{t("live.signedOut")}</span>
        ) : live ? (
          <>
            <span className="text-body-sm">
              {t("live.joinCode")}: <strong className="tabular-nums">{live.code}</strong>
            </span>
            <input
              readOnly
              value={`${location.origin}/j/${live.code}`}
              onFocus={(e) => e.currentTarget.select()}
              aria-label={t("live.joinLink")}
              className="rounded-md border border-border bg-surface p-2 text-body-sm"
            />
            <div className="flex flex-wrap items-center gap-3">
              <a href={`/projecao/${live.id}`} target="_blank" rel="noreferrer" className="rounded-md bg-surface px-3 py-1 text-body-sm">
                {t("live.projection")}
              </a>
              <button onClick={() => attachLive(null)} className="rounded-md bg-surface px-3 py-1 text-body-sm">
                {t("live.detach")}
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => void createLive()}
              disabled={busy}
              className="rounded-md bg-primary px-3 py-1 font-bold text-primary-foreground disabled:opacity-50"
            >
              {t("live.create")}
            </button>
            <label className="flex items-center gap-2 text-body-sm">
              {t("live.codeLabel")}
              <input
                value={typedCode}
                onChange={(e) => setTypedCode(e.target.value)}
                className="w-28 rounded-md border border-border p-1 uppercase"
                aria-label={t("live.codeLabel")}
              />
            </label>
            <button onClick={() => void attachByCode()} disabled={busy} className="rounded-md bg-surface px-3 py-1 disabled:opacity-50">
              {t("live.attach")}
            </button>
          </div>
        )}
        {liveError && (
          <p role="alert" className="text-body-sm text-warning-foreground">
            {liveError}
          </p>
        )}
      </div>

      {saved !== null && saved.length > 0 && (
        <details className="no-print rounded-md border border-border p-3">
          <summary className="cursor-pointer font-bold">
            {t("planner.myAgendas")} ({saved.length})
          </summary>
          <ul className="mt-3 flex flex-col gap-2">
            {saved.map((a) => (
              <li key={a.id} className="flex items-center gap-3 text-body-sm">
                <span className="flex-1">
                  {a.title}
                  <span className="text-muted">
                    {" "}
                    · {a.blockCount} · {a.totalMin} {t("planner.min")}
                  </span>
                  {a.shared && (
                    <span className="ml-2 rounded-full bg-surface px-2 py-1" title={t("planner.shareHint")}>
                      🔗 {t("planner.sharedBadge")}
                    </span>
                  )}
                </span>
                <button onClick={() => void open(a.id)} disabled={busy} className="rounded-md bg-surface px-2 py-1 disabled:opacity-50">
                  {t("planner.open")}
                </button>
                <button
                  onClick={() => void discard(a.id)}
                  disabled={busy}
                  className="bg-warning text-warning-foreground rounded-md px-2 py-1 disabled:opacity-50"
                  aria-label={`${t("planner.delete")} ${a.title}`}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="flex justify-between rounded-md bg-surface p-3 font-bold">
        <span>{t("planner.total")}</span>
        <span>
          {scheduled.totalMin} {t("planner.min")} · {agenda.startClock}–{scheduled.endClock}
        </span>
      </div>

      {agenda.blocks.length > 0 && <DeliveryClockPanel blocks={agenda.blocks} />}

      {agenda.blocks.length === 0 ? (
        <p className="text-muted">{t("planner.empty")}</p>
      ) : (
        <>
          <p className="no-print text-body-sm text-muted">{t("planner.dragHint")}</p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={agenda.blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <ol className="flex flex-col gap-2">
                {scheduled.blocks.map((b) => (
                  <SortableBlock
                    key={b.id}
                    id={b.id}
                    title={b.title}
                    startClock={b.startClock ?? ""}
                    duration={b.durationMin}
                    onDuration={(d) => setDuration(b.id, d)}
                    facilitatorOnly={b.facilitatorOnly === true}
                    onFacilitatorOnly={() => toggleFacOnly(b.id)}
                    onRemove={() => remove(b.id)}
                    run={
                      <RunControl
                        state={runState(specOf(b), live, saved !== null)}
                        busy={busy}
                        run={runs[b.id]}
                        onRun={() => void runBlock(b)}
                        onClose={() => void closeRound(b.id)}
                        hostedOptions={hostedOptionsOf(specOf(b))}
                        onRecord={(optionId) => void closeRound(b.id, optionId)}
                        results={resultsState(specOf(b), live, saved !== null)}
                        resultsOpen={openBlock === b.id}
                        onResults={() => void toggleResults(b.id)}
                      />
                    }
                    panel={
                      openBlock === b.id && (
                        <ResultsPanel
                          rounds={sessionResults === null ? [] : roundsForBlock(sessionResults.rounds, b.id)}
                          withheld={sessionResults?.withheldByRetention ?? 0}
                          retentionDays={sessionResults?.retentionDays ?? 0}
                          spec={specOf(b)}
                          error={resultsError}
                          loading={sessionResults === null && resultsError === null}
                        />
                      )
                    }
                  />
                ))}
              </ol>
            </SortableContext>
          </DndContext>
        </>
      )}
    </KitShell>
  );
}

function SortableBlock(props: {
  id: string;
  title: string;
  startClock: string;
  duration: number;
  onDuration: (d: number) => void;
  /** THE-1698: hidden from the share link and from what a visitor prints or downloads. */
  facilitatorOnly: boolean;
  onFacilitatorOnly: () => void;
  onRemove: () => void;
  /** The block's live control, built by the planner — this component stays about the row. */
  run: React.ReactNode;
  /** Expanded under the row: the results panel, when this block's is open. */
  panel?: React.ReactNode;
}): React.ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });
  const style: React.CSSProperties = { transform: CSS.Transform.toString(transform), transition };
  return (
    <li ref={setNodeRef} style={style} className="flex flex-col gap-2 rounded-md border border-border bg-background p-3">
      <div className="flex items-center gap-3">
        <button {...attributes} {...listeners} className="no-print cursor-grab text-muted" aria-label="drag">⠿</button>
        <span className="w-14 font-bold tabular-nums">{props.startClock}</span>
        <span className="flex-1">{props.title}</span>
        <label className="no-print flex items-center gap-1 text-body-sm text-muted" title={t("planner.facOnlyHint")}>
          <input type="checkbox" checked={props.facilitatorOnly} onChange={props.onFacilitatorOnly} />
          {t("planner.facOnly")}
        </label>
        {props.facilitatorOnly && (
          <span className="print-only rounded-md bg-surface px-2 py-0.5 text-body-sm">{t("planner.facOnly")}</span>
        )}
        <input
          type="number"
          min={0}
          value={props.duration}
          onChange={(e) => props.onDuration(Number(e.target.value))}
          className="w-16 rounded-md border border-border p-1 text-right"
          aria-label="duration"
        />
        <span className="text-body-sm text-muted">{t("planner.min")}</span>
        {props.run}
        <button onClick={props.onRemove} className="no-print text-warning-foreground bg-warning rounded-md px-2 py-1" aria-label={t("planner.remove")}>
          ✕
        </button>
      </div>
      {props.panel}
    </li>
  );
}

/**
 * What the room answered for THIS block, newest run first (US-MB-6 fatia 2, AC3).
 *
 * Every run is listed, not just the latest: `qidFor` gives each one its own id, so a block run twice
 * has two rounds — and the earlier tally is usually why it was re-run. The newest is expanded, the
 * rest collapse to their own summary line.
 *
 * A dumb map over `rowsFor`, which is where the per-type rules live and are tested. Retention is
 * live-api's and is only REPORTED here (`results.withheld`, the same sentence the results screen
 * uses) — never worked around.
 */
function ResultsPanel(props: {
  rounds: RoundResult[];
  withheld: number;
  retentionDays: number;
  spec: RoundSpec | null;
  error: string | null;
  loading: boolean;
}): React.ReactElement {
  if (props.error !== null) {
    return (
      <p role="alert" className="text-body-sm text-warning-foreground">
        {props.error}
      </p>
    );
  }
  if (props.loading) return <p className="text-body-sm text-muted">{t("results.loading")}</p>;
  return (
    <div className="no-print flex flex-col gap-2 rounded-md bg-surface p-3">
      {props.rounds.length === 0 ? (
        <span className="text-body-sm text-muted">{t("live.noRounds")}</span>
      ) : (
        props.rounds.map((round, i) => (
          <details key={`${round.ord}`} open={i === 0} className="text-body-sm">
            <summary className="cursor-pointer">{roundMeta(round, getLocale())}</summary>
            <ul className="mt-1 flex flex-col gap-1">
              {rowsFor(round, props.spec).map((row, j) => (
                <li key={`${row.label}-${j}`} className="flex justify-between gap-3">
                  <span>{row.label}</span>
                  <span className="tabular-nums">{row.value}</span>
                </li>
              ))}
            </ul>
          </details>
        ))
      )}
      {props.withheld > 0 && (
        <span className="text-body-sm text-muted">
          {t("results.withheld", { n: String(props.withheld), days: String(props.retentionDays) })}
        </span>
      )}
    </div>
  );
}

/**
 * "Rodar agora" for one block.
 *
 * Disabled with a caption rather than hidden: a facilitator who never sees the button never learns
 * that a card can BE a round, and "why is it greyed out" is a question the caption answers.
 */
function RunControl(props: {
  state: RunState;
  busy: boolean;
  run: BlockRun | undefined;
  onRun: () => void;
  onClose: () => void;
  /** Single-screen block (US-LV-22): one tap per option records the room's choice and closes. */
  hostedOptions: Array<{ id: string; label: string }>;
  onRecord: (optionId: string) => void;
  results: ResultsState;
  resultsOpen: boolean;
  onResults: () => void;
}): React.ReactElement {
  const { state, run } = props;
  // The state IS the key suffix (`live.signedOut`, `live.noSpec`, `live.noSession`) — renaming a
  // state without its key renders the raw key, which the parity test would not catch. Same rule for
  // `ResultsState`, whose one extra member (`notRecorded`) has its own key.
  const caption = state === "ready" ? null : t(`live.${state}`);
  // An arm on a block that stopped being runnable (signed out, the card lost its spec, the room was
  // detached) says nothing: "rode quando quiser" beside a disabled button is an instruction the
  // facilitator cannot follow, and the caption below already says why the button is dead.
  const armed = showsArm(run, state);
  const resultsCaption = props.results === "ready" ? null : t(`live.${props.results}`);
  return (
    <span className="no-print flex flex-col items-end gap-1">
      <span className="flex items-center gap-2">
        <button
          onClick={props.onRun}
          disabled={state !== "ready" || props.busy}
          title={caption ?? undefined}
          className={`rounded-md bg-surface px-2 py-1 text-body-sm disabled:opacity-50${armed ? " font-bold" : ""}`}
        >
          {t("live.run")}
        </button>
        {/* Only over the round the room is actually holding — closing anything else would close it. */}
        {run?.kind === "live" &&
          props.hostedOptions.map((o) => (
            <button
              key={o.id}
              onClick={() => props.onRecord(o.id)}
              disabled={props.busy}
              className="rounded-md bg-primary px-2 py-1 text-body-sm font-bold text-primary-foreground disabled:opacity-50"
            >
              {t("hosted.record", { label: o.label })}
            </button>
          ))}
        {run?.kind === "live" && (
          <button onClick={props.onClose} disabled={props.busy} className="rounded-md bg-surface px-2 py-1 text-body-sm disabled:opacity-50">
            {t("live.close")}
          </button>
        )}
        {/* Offered whenever a room is attached, not only after a run: "nothing yet" is an answer the
            panel gives (`live.noRounds`), and a button that appears only once something has run is a
            button nobody finds. Disabled with its caption, like "Rodar agora". */}
        <button
          onClick={props.onResults}
          disabled={props.results !== "ready" || props.busy}
          aria-expanded={props.resultsOpen}
          title={resultsCaption ?? undefined}
          className="rounded-md bg-surface px-2 py-1 text-body-sm disabled:opacity-50"
        >
          {t("live.results")}
        </button>
      </span>
      {caption && <span className="text-body-sm text-muted">{caption}</span>}
      {/* Only when it is not the same sentence the run caption already shows (signed out, no room). */}
      {resultsCaption !== null && props.results === "notRecorded" && (
        <span className="text-body-sm text-muted">{resultsCaption}</span>
      )}
      {run?.kind === "live" && (
        <span role="status" className="text-body-sm font-bold">
          {run.timeLimitMs === null ? t("live.onAir") : t("live.onAirTimed", { min: String(Math.round(run.timeLimitMs / 60_000)) })}
        </span>
      )}
      {/* Announced, because the close that armed it happened with no click here — a facilitator on a
          screen reader would otherwise learn nothing (`live.onAir` is announced for the same reason). */}
      {armed && (
        <span role="status" className="text-body-sm font-bold">
          {t("live.armed")}
        </span>
      )}
      {run?.kind === "superseded" && <span className="text-body-sm text-muted">{t("live.superseded")}</span>}
      {run?.kind === "closed" && <span className="text-body-sm text-muted">{t("live.closedRound")}</span>}
      {run?.kind === "unknown" && (
        <span role="alert" className="text-body-sm text-warning-foreground">
          {t("live.unknownOutcome")}
        </span>
      )}
      {run?.kind === "failed" && (
        <span role="alert" className="text-body-sm text-warning-foreground">
          {run.message}
        </span>
      )}
    </span>
  );
}

/**
 * Delivery clock (THE-1444): real time against the plan, while the facilitator runs the agenda.
 *
 * Local state only — nothing is saved, a reload ends the run. Its own component so the one-second
 * tick re-renders this panel and not the whole planner. The spoken sentence is set only when the
 * block or the state CHANGES (see `describeDelivery`), never per tick.
 */
function DeliveryClockPanel(props: { blocks: readonly AgendaBlockInput[] }): React.ReactElement {
  const [startedAt, setStartedAt] = React.useState<number | null>(null);
  const [current, setCurrent] = React.useState<{ blockId: string; startedAtMs: number } | null>(null);
  const [now, setNow] = React.useState(() => Date.now());
  const [spoken, setSpoken] = React.useState("");

  React.useEffect(() => {
    if (startedAt === null) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);

  const clock: DeliveryClock | null = startedAt === null ? null : deliveryClock(props.blocks, { startedAtMs: startedAt, nowMs: now, current });
  const key = clock === null ? "" : `${clock.phase}|${clock.block?.id ?? ""}|${clock.state}|${clock.blockOverrunMin > 0}`;
  React.useEffect(() => {
    setSpoken(clock === null ? "" : describeDelivery(clock));
    // Keyed on the change, not on `clock`: a new object every tick would re-announce every second.
  }, [key]);

  const start = (): void => {
    const at = Date.now();
    setStartedAt(at);
    setNow(at);
    setCurrent(props.blocks[0] ? { blockId: props.blocks[0].id, startedAtMs: at } : null);
  };
  const next = (): void => {
    if (clock === null || clock.phase !== "running") return;
    const after = props.blocks[clock.index + 1];
    if (after) setCurrent({ blockId: after.id, startedAtMs: Date.now() });
  };
  const stop = (): void => {
    setStartedAt(null);
    setCurrent(null);
  };

  const over = clock !== null && (clock.state === "over" || clock.blockOverrunMin > 0);
  return (
    <section aria-label={t("planner.clock.title")} className="no-print flex flex-col gap-2 rounded-md bg-surface p-3">
      <LiveRegion text={spoken} label={t("planner.clock.live")} />
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-bold">{t("planner.clock.title")}</h2>
        {clock === null ? (
          <button onClick={start} className="rounded-md bg-primary px-2 py-1 text-primary-foreground">
            {t("planner.clock.start")}
          </button>
        ) : (
          <>
            {clock.phase === "running" && clock.index < props.blocks.length - 1 && (
              <button onClick={next} className="rounded-md bg-surface px-2 py-1">
                {t("planner.clock.next")}
              </button>
            )}
            <button onClick={stop} className="rounded-md bg-surface px-2 py-1">
              {t("planner.clock.stop")}
            </button>
          </>
        )}
      </div>
      {clock !== null && (
        <p className={over ? "rounded-md bg-warning p-2 text-warning-foreground" : "text-body-sm"}>
          {clock.phase === "done" ? (
            t("planner.clock.done")
          ) : (
            <>
              <strong>{t("planner.clock.onAir", { block: clock.block?.title ?? "" })}</strong>
              {" · "}
              {clock.blockOverrunMin > 0
                ? `⚠ ${t("planner.clock.blockOver", { n: String(clock.blockOverrunMin) })}`
                : t("planner.clock.remaining", { n: String(clock.blockRemainingMin) })}
              {" · "}
              {clock.state === "over"
                ? `⚠ ${t("planner.clock.agendaOver", { n: String(clock.agendaOverrunMin) })}`
                : clock.state === "ahead"
                  ? t("planner.clock.agendaAhead", { n: String(clock.agendaAheadMin) })
                  : t("planner.clock.onTime")}
            </>
          )}
        </p>
      )}
    </section>
  );
}
